/**
 * Listening module data transformation utilities.
 *
 * Transforms backend SimulationData structure to frontend Question types
 * organized by sections. For listening module, questions are grouped by
 * section and flattened from questionGroups.
 *
 * @module listeningDataTransform
 */

import { transformQuestion } from './questionTransform';
import type {
  SimulationData,
  Question,
} from '@/types/exam-simulation';


/**
 * Transform backend simulation data to frontend format for listening module.
 *
 * Converts the backend SimulationData structure (with sections, question groups,
 * and contexts) into the frontend format with Questions organized by section.
 * Preserves question group and context information for auto audio playback.
 *
 * @param data - Backend simulation data containing sections with question groups
 * @returns Object containing:
 *   - `sections`: Array of section objects with section number, title, questions, and context audio info
 *   - `allQuestions`: Array of all Question objects with sequential IDs
 *
 * @example
 * ```typescript
 * const { sections, allQuestions } = transformListeningData(simulationData);
 * // sections[0] contains section 1 with its questions and context audio URLs
 * // allQuestions contains all questions with sequential IDs
 * ```
 */
export function transformListeningData(data: SimulationData): {
  sections: Array<{
    section: number;
    title: string;
    questions: Question[];
    material_type?: 'audio' | null;
    material_url?: string | null;
    material_binary?: string | null;
    material_mime_type?: string | null;
    questionGroups: Array<{
      contextId: string;
      context: {
        id: number;
        name: string;
        type: string;
        mediaUrl?: string | null;
        mimeType?: string | null;
        binaryContent?: string | null;
      } | null;
      questions: Question[];
    }>;
  }>;
  allQuestions: Question[];
} {
  const sections: Array<{
    section: number;
    title: string;
    questions: Question[];
    material_type?: 'audio' | null;
    material_url?: string | null;
    material_binary?: string | null;
    material_mime_type?: string | null;
    questionGroups: Array<{
      contextId: string;
      context: {
        id: number;
        name: string;
        type: string;
        mediaUrl?: string | null;
        mimeType?: string | null;
        binaryContent?: string | null;
      } | null;
      questions: Question[];
    }>;
  }> = [];
  const allQuestions: Question[] = [];
  let currentQuestionId = 1;

  // Transform each section
  for (const section of data.sections) {
    const questionGroups: Array<{
      contextId: string;
      context: {
        id: number;
        name: string;
        type: string;
        mediaUrl?: string | null;
        mimeType?: string | null;
        binaryContent?: string | null;
      } | null;
      questions: Question[];
    }> = [];
    const sectionQuestions: Question[] = [];

    // Process each question group in the section
    for (const group of section.questionGroups) {
      const groupQuestions: Question[] = [];
      
      // Transform all questions in this group
      for (const simQuestion of group.questions) {
        // Listening module: paragraphRef defaults to 1, section is provided
        const question = transformQuestion(simQuestion, currentQuestionId, 1, section.section);
        groupQuestions.push(question);
        sectionQuestions.push(question);
        currentQuestionId++;
      }

      // Preserve context information for audio playback
      questionGroups.push({
        contextId: group.contextId,
        context: group.context
          ? {
              id: group.context.id,
              name: group.context.name,
              type: group.context.type,
              mediaUrl: group.context.mediaUrl || null,
              mimeType: group.context.mimeType || null,
              binaryContent: group.context.binaryContent || null,
            }
          : null,
        questions: groupQuestions,
      });
    }
    
    sections.push({
      section: section.section,
      title: section.title || `Section ${section.section}`,
      questions: sectionQuestions,
      material_type: section.material_type,
      material_url: section.material_url,
      material_binary: section.material_binary,
      material_mime_type: section.material_mime_type,
      questionGroups,
    });
    
    allQuestions.push(...sectionQuestions);
  }

  return { sections, allQuestions };
}

